import React, { useState, useEffect, useCallback } from "react";
import {
  Archive, Users, Loader2, RotateCcw, Trash2, AlertTriangle, Calendar, Copy
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Team-Hub card: Archive & Duplicates
 *   1) Archived employees — recoverable for 90 days, then purged
 *      Restore in one click; or purge immediately if certain
 *   2) Duplicate detection — scans for overlapping email/phone/name across the active team
 */
const ArchiveAndDuplicatesPanel = ({ authAxios }) => {
  const [archived, setArchived] = useState({ items: [], total: 0, recovery_window_days: 90 });
  const [dupes, setDupes] = useState({ groups: [], total_groups: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, d] = await Promise.all([
        authAxios.get(`${API_URL}/api/admin/managers/archived`),
        authAxios.get(`${API_URL}/api/admin/managers/duplicates`),
      ]);
      setArchived(a.data);
      setDupes(d.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const restore = async (m) => {
    if (!window.confirm(`Restore @${m.username} back to the active team?`)) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/managers/${m.username}/restore`);
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Restore failed");
    }
  };

  const purge = async (m) => {
    const ok = window.confirm(
      `PERMANENT DELETE for @${m.username}.\n\n` +
      `• All records removed (employee doc + policy signatures)\n` +
      `• This CANNOT be undone\n\n` +
      `Type OK in the next prompt to confirm.`
    );
    if (!ok) return;
    const typed = window.prompt("Type 'PURGE' to confirm permanent deletion:");
    if (typed !== "PURGE") { alert("Cancelled — text didn't match."); return; }
    try {
      await authAxios.delete(`${API_URL}/api/admin/managers/${m.username}/purge`);
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Purge failed");
    }
  };

  const archiveFromDupe = async (m) => {
    if (!window.confirm(`Archive @${m.username}? 90-day recovery window applies.`)) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/managers/${m.username}`);
      fetchAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Archive failed");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="archive-dupes-panel">
      {/* ── Archived ── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="archived-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#475569] to-[#1E1B4B] flex items-center justify-center">
            <Archive size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-[#1E1B4B] text-sm">Archived Employees</h3>
            <p className="text-xs text-[#94a3b8]">
              {archived.total} archived · recoverable for {archived.recovery_window_days} days, then permanently purged
            </p>
          </div>
        </div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
        ) : archived.items.length === 0 ? (
          <div className="py-6 text-center text-xs text-gray-400">Nothing archived. Use the Archive button on a staff row if someone leaves.</div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {archived.items.map((m) => (
              <div key={m.username} className="border border-gray-100 rounded-xl p-3" data-testid={`archived-row-${m.username}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1E1B4B] truncate">{m.name} <span className="text-[10px] font-mono text-gray-400">@{m.username}</span></p>
                    <p className="text-[10px] text-gray-500">Role: {m.role} · Dept: {m.department}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                      <span className="flex items-center gap-0.5"><Calendar size={9} /> archived {m.deleted_at ? new Date(m.deleted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "?"}</span>
                      {m.days_until_purge != null && (
                        <span className={`px-1.5 py-0.5 rounded font-bold ${m.days_until_purge <= 7 ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                          {m.days_until_purge}d until purge
                        </span>
                      )}
                      {m.deleted_by && <span className="text-gray-400">by {m.deleted_by}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => restore(m)}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600 flex items-center justify-center gap-1"
                    data-testid={`restore-${m.username}`}
                  >
                    <RotateCcw size={12} /> Restore
                  </button>
                  <button
                    onClick={() => purge(m)}
                    className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold hover:bg-red-600 flex items-center gap-1"
                    data-testid={`purge-${m.username}`}
                  >
                    <Trash2 size={12} /> Purge
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Duplicates ── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="duplicates-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#DC283C] flex items-center justify-center">
            <Copy size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-[#1E1B4B] text-sm">Duplicate Detector</h3>
            <p className="text-xs text-[#94a3b8]">
              {dupes.total_groups > 0
                ? `${dupes.total_groups} duplicate group${dupes.total_groups === 1 ? "" : "s"} found — review & archive extras`
                : "No duplicates detected. Clean team list ✨"}
            </p>
          </div>
        </div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
        ) : dupes.total_groups === 0 ? (
          <div className="py-6 text-center text-xs text-gray-400">
            <Users size={26} className="mx-auto text-gray-300 mb-2" />
            No overlap in email, phone, or name across your active team.
          </div>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {dupes.groups.map((g, gi) => (
              <div key={`${g.match_type}-${g.match_value || gi}`} className="border border-amber-200 bg-amber-50/30 rounded-xl p-3" data-testid={`dup-group-${gi}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={12} className="text-amber-600" />
                  <p className="text-[11px] font-bold text-amber-700">
                    Match on {g.match_type}: <span className="font-mono">{g.match_value}</span>
                  </p>
                  <span className="ml-auto text-[10px] text-gray-500">{g.count} records</span>
                </div>
                <div className="space-y-1.5">
                  {g.records.map((r) => (
                    <div key={r.username} className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg p-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-[#1E1B4B] truncate">{r.name} <span className="text-[10px] font-mono text-gray-400">@{r.username}</span></p>
                        <p className="text-[10px] text-gray-500">{r.role} · {r.department} · created {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "?"}</p>
                      </div>
                      <button
                        onClick={() => archiveFromDupe(r)}
                        className="px-2 py-1 rounded bg-red-50 text-red-600 text-[10px] font-bold hover:bg-red-100 flex items-center gap-1 flex-shrink-0"
                        data-testid={`dup-archive-${r.username}`}
                      >
                        <Archive size={10} /> Archive
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchiveAndDuplicatesPanel;
